// ============================================
// FILE: src/components/delivery/PincodeChecker.jsx
// ============================================
//
// "Check delivery" box for the product page: enter a pincode, see whether we
// deliver there, how fast, at what fee and whether pay-on-delivery is offered.
// The last checked pincode is remembered, so it is asked once, not per product.

import React, { useState, useEffect } from 'react';
import { Truck, Banknote, AlertCircle } from 'lucide-react';
import apiService from '../../apiService';
import {
  isValidPincode, formatEta, describeShipping, getLastPincode, rememberPincode,
} from '../../utils/deliveryHelpers';

const PincodeChecker = ({ onResult }) => {
  const [pincode, setPincode] = useState(getLastPincode);
  const [delivery, setDelivery] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');

  const report = (result) => {
    setDelivery(result);
    if (typeof onResult === 'function') onResult(result);
  };

  const check = async (value) => {
    if (!isValidPincode(value)) {
      setError('Enter a valid 6-digit pincode');
      report(null);
      return;
    }

    setChecking(true);
    setError('');
    const response = await apiService.checkDeliveryPincode(value);
    setChecking(false);

    if (response.success && response.data?.delivery) {
      rememberPincode(value);
      report(response.data.delivery);
    } else {
      setError(response.status === 429 ? 'Too many checks. Please try again in a minute.' : 'Could not check this pincode. Please try again.');
      report(null);
    }
  };

  // Re-check the remembered pincode on open, so returning shoppers see the
  // answer without pressing the button again.
  useEffect(() => {
    if (isValidPincode(pincode)) check(pincode);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const line = (Icon, text, color = 'var(--fx-ink-2)') => (
    <div className="flex items-center gap-2.5">
      <Icon size={16} style={{ color: color === 'var(--fx-ink-2)' ? 'var(--fx-ink-3)' : color, flexShrink: 0 }} />
      <span style={{ fontSize: 14, color }}>{text}</span>
    </div>
  );

  return (
    <div className="mb-4">
      <form
        className="flex gap-2"
        style={{ maxWidth: 320 }}
        onSubmit={(e) => { e.preventDefault(); check(pincode); }}
      >
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={pincode}
          onChange={(e) => {
            if (!/^\d{0,6}$/.test(e.target.value)) return;
            setPincode(e.target.value);
            setError('');
            report(null);
          }}
          placeholder="Enter pincode"
          aria-label="Delivery pincode"
          className="flex-1 min-w-0 px-3 py-2 outline-none"
          style={{ border: '1px solid var(--fx-line)', borderRadius: 2, fontSize: 14 }}
        />
        <button
          type="submit"
          disabled={checking}
          className="px-4"
          style={{ fontSize: 13, fontWeight: 700, color: 'var(--fx-pink)' }}
        >
          {checking ? 'CHECKING…' : 'CHECK'}
        </button>
      </form>

      {error && <p className="mt-2" style={{ fontSize: 13, color: 'var(--fx-pink)' }}>{error}</p>}

      {delivery && (
        <div className="mt-3 space-y-2.5">
          {delivery.serviceable === false ? (
            line(AlertCircle, `Sorry, we don't deliver to ${pincode} yet`, 'var(--fx-pink)')
          ) : (
            <>
              {line(Truck, [formatEta(delivery) || 'Delivery available', describeShipping(delivery)].filter(Boolean).join(' · '))}
              {line(Banknote, delivery.cod_enabled === false ? 'Pay on delivery not available' : 'Pay on delivery available')}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PincodeChecker;
